import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Save } from "lucide-react";

/**
 * v5.7 Addition 2 — Additional Details card on Deal Detail Overview.
 * Resolves the current pipeline's custom Deal fields live and renders an editor
 * for each. Values persist via /fields/values/{deal_id}.
 */
export default function AdditionalDetails({ dealId, pipelineId }) {
  const [fields, setFields] = useState([]);
  const [values, setValues] = useState({});
  const [dirty, setDirty] = useState({});

  const load = useCallback(async () => {
    if (!pipelineId || !dealId) return;
    const [{ data: live }, { data: vals }] = await Promise.all([
      api.get("/fields/live", { params: { pipeline_id: pipelineId, module: "deal" } }),
      api.get("/fields/values", { params: { record_id: dealId, module: "deal", pipeline_id: pipelineId } }),
    ]);
    // Only render custom (attached) fields in this card. Core fields are handled elsewhere.
    const attached = live.filter((f) => f.is_attachment && f.is_custom);
    setFields(attached);
    const map = {};
    for (const v of vals) {
      map[v.field_definition_id] =
        v.value_text ??
        v.value_number ??
        v.value_date ??
        v.value_boolean ??
        v.value_array ??
        "";
    }
    setValues(map);
  }, [pipelineId, dealId]);

  useEffect(() => { load(); }, [load]);

  const setVal = (fid, v) => {
    setValues((prev) => ({ ...prev, [fid]: v }));
    setDirty((prev) => ({ ...prev, [fid]: true }));
  };

  const save = async (f) => {
    try {
      await api.post(`/fields/values/${dealId}`, {
        field_definition_id: f.field_definition_id,
        module: "deal",
        value: values[f.field_definition_id],
      });
      setDirty((prev) => ({ ...prev, [f.field_definition_id]: false }));
      toast.success(`${f.effective_label} saved`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  if (fields.length === 0) return null;

  return (
    <div className="cw-card p-5" data-testid="additional-details-card">
      <h3 className="cw-h3">Additional Details</h3>
      <div className="mt-4 space-y-3">
        {fields.map((f) => (
          <FieldRow
            key={f.field_definition_id}
            field={f}
            value={values[f.field_definition_id]}
            onChange={(v) => setVal(f.field_definition_id, v)}
            onSave={() => save(f)}
            dirty={!!dirty[f.field_definition_id]}
          />
        ))}
      </div>
    </div>
  );
}

function FieldRow({ field, value, onChange, onSave, dirty }) {
  const commonProps = {
    className: "w-full h-9 px-3 rounded-md border text-sm",
    style: { borderColor: "var(--cw-border)" },
    value: value ?? "",
    onChange: (e) => onChange(e.target.value),
    onBlur: dirty ? onSave : undefined,
    "data-testid": `cf-${field.field_key}`,
  };
  let input;
  const type = field.field_type;
  const isComputed = type === "computed";
  if (isComputed) {
    input = (
      <div className="flex items-center gap-2">
        <div
          className="flex-1 h-9 px-3 rounded-md border text-sm bg-gray-50 flex items-center"
          style={{ borderColor: "var(--cw-border)", color: "var(--cw-body)" }}
          data-testid={`cf-${field.field_key}`}
        >
          {value ?? "—"}
        </div>
        <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded"
          style={{ background: "rgba(40,42,116,0.1)", color: "var(--cw-primary)" }}>
          Computed
        </span>
      </div>
    );
  } else if (type === "multi_line") {
    input = <textarea {...commonProps} className="w-full px-3 py-2 rounded-md border text-sm min-h-[80px]" />;
  } else if (type === "checkbox") {
    input = (
      <input
        type="checkbox"
        checked={!!value}
        onChange={(e) => {
          onChange(e.target.checked);
          setTimeout(onSave, 0);
        }}
        data-testid={`cf-${field.field_key}`}
      />
    );
  } else if (type === "date") {
    input = <input type="date" {...commonProps} />;
  } else if (type === "number" || type === "decimal" || type === "currency" || type === "percent") {
    input = <input type="number" {...commonProps} />;
  } else if (type === "url" || type === "email" || type === "phone") {
    input = <input type={type === "phone" ? "tel" : type} {...commonProps} />;
  } else if (type === "dropdown" && Array.isArray(field.options)) {
    input = (
      <select {...commonProps}>
        <option value="">—</option>
        {field.options.map((o) => (
          <option key={o.option_key} value={o.option_key}>{o.option_label}</option>
        ))}
      </select>
    );
  } else {
    input = <input type="text" {...commonProps} />;
  }
  return (
    <div className="flex items-start gap-3">
      <div className="w-40 shrink-0">
        <div className="cw-caption">
          {field.effective_label}
          {field.effective_required && <span className="text-red-500 ml-0.5">*</span>}
        </div>
      </div>
      <div className="flex-1">{input}</div>
      {dirty && !isComputed && (
        <button
          onClick={onSave}
          className="cw-btn-secondary shrink-0 flex items-center gap-1"
          data-testid={`cf-save-${field.field_key}`}
        >
          <Save size={12} /> Save
        </button>
      )}
    </div>
  );
}
